import { baseApi } from '@/shared/api'
import { DEFAULT_PAGE_SIZE } from '@/shared/lib'
import type {
  BuildActing,
  BuildLineList,
  BuildLinesQuery,
  BuildProductList,
  BuildProductsQuery,
  BuildTreeNode,
  BuildTreeQuery,
  ChangeBuildDraftQuantityBody,
  ChangeBuildDraftQuantityResult,
  ConfirmBuildBody,
  ConfirmBuildResult,
  CreateBuildBody,
  CreateBuildResult,
  OrderBuildBody,
  OrderBuildResult,
  RemoveBuildBody,
  RemoveBuildResult,
  WriteOffBuildBody,
  WriteOffBuildResult,
} from '../model/types'

/** Модификатор уходит строкой запроса и у записи: тело — полезная нагрузка действия. */
const actingParams = (acting: BuildActing | undefined) => ({
  ...(acting?.client !== undefined ? { client: acting.client } : {}),
  ...(acting?.account !== undefined ? { account: acting.account } : {}),
})

/** Список сборок — один тег: любая правка сборки перечитывает его целиком. */
const LIST = [{ type: 'PcBuild' as const, id: 'LIST' }]
/** Корзина меняется при сборке из корзины: итоги и счётчик в шапке едут за ней. */
const CART = [
  { type: 'Cart' as const, id: 'LIST' },
  { type: 'Cart' as const, id: 'SUMMARY' },
]

export const pcBuildApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Строки сборок деревом: сборка, её составляющие, самостоятельные позиции того же документа.
     * `total` считается в документах, а не в строках; порядок и срез — сервер (правило 11).
     * `draft: 'true'` — только неподтверждённые. У менеджера глазами клиента — `client`/`account`.
     */
    getBuilds: build.query<BuildLineList, BuildLinesQuery>({
      query: ({ page = 1, pageSize = DEFAULT_PAGE_SIZE, ...rest }) => ({
        url: '/pc-build',
        params: { page, pageSize, ...rest },
      }),
      providesTags: LIST,
    }),

    /**
     * Дерево набора: без `categoryId` — 13 корневых категорий, с ним — марки категории.
     * Уровень приходит массивом целиком (пагинации нет).
     */
    getBuildTree: build.query<BuildTreeNode[], BuildTreeQuery>({
      query: (params) => ({ url: '/pc-build/tree', params }),
      providesTags: [{ type: 'PcBuild', id: 'TREE' }],
    }),

    /**
     * Грид товаров набора (`categoryId` обязателен): свой склад и заказной каталог одной выдачей.
     * Отбор по марке и поиску, сортировка, страница и `total` — сервер; цена — вкладка `settlement`.
     */
    getBuildProducts: build.query<BuildProductList, BuildProductsQuery>({
      query: ({ page = 1, pageSize = DEFAULT_PAGE_SIZE, ...rest }) => ({
        url: '/pc-build/products',
        params: { page, pageSize, ...rest },
      }),
      providesTags: [{ type: 'PcBuild', id: 'PRODUCTS' }],
    }),

    /**
     * Собрать из корзины: позиции раздела `pc-build` корзины становятся черновиком сборки
     * (`OZ…`), в 1С он не уходит (`commandId` нет). Пустой `cartItemIds` — весь раздел.
     */
    createBuildFromCart: build.mutation<CreateBuildResult, CreateBuildBody>({
      query: (body) => ({ url: '/pc-build/from-cart', method: 'POST', body }),
      invalidatesTags: [...LIST, ...CART],
    }),

    /**
     * Подтвердить сборку: проверка комплектности, отправка в 1С. Неполная сборка отклоняется
     * (`409 BUILD_INCOMPLETE`/`BUILD_NO_GPU`/`BUILD_EMPTY`, чего не хватает — в `fields.missing`),
     * пока клиент не повторит с `force`.
     */
    confirmBuild: build.mutation<ConfirmBuildResult, { acting?: BuildActing } & ConfirmBuildBody>({
      query: ({ acting, ...body }) => ({
        url: '/pc-build/confirm',
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LIST,
    }),

    /** Заказать сборку: доступно одной сборке в `stat2`, иначе `409`; переходит в `stat3`. */
    orderBuild: build.mutation<OrderBuildResult, { acting?: BuildActing } & OrderBuildBody>({
      query: ({ acting, ...body }) => ({
        url: '/pc-build/order',
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LIST,
    }),

    /**
     * Списать позиции сборки: только «Готов» (`stat4`); нет ни одной — `409 WRITE_OFF_NOT_READY`.
     * Только клиент: модификатора ручка не принимает.
     */
    writeOffBuild: build.mutation<WriteOffBuildResult, WriteOffBuildBody>({
      query: (body) => ({ url: '/pc-build/write-off', method: 'POST', body }),
      invalidatesTags: LIST,
    }),

    /**
     * Правка количества строк черновика: правится сразу, без 1С (документа там ещё нет).
     * Сборку в производстве править нельзя — `409 BUILD_IN_PRODUCTION`.
     */
    changeBuildDraftQuantity: build.mutation<
      ChangeBuildDraftQuantityResult,
      { acting?: BuildActing } & ChangeBuildDraftQuantityBody
    >({
      query: ({ acting, ...body }) => ({
        url: '/pc-build/lines',
        method: 'PATCH',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LIST,
    }),

    /**
     * Удалить строки черновика/сборки: черновик — молча; отправленная сборка уходит в 1С, затем
     * строки убираются. Сборку в производстве удалить нельзя — `409 BUILD_IN_PRODUCTION`.
     */
    removeBuildLines: build.mutation<RemoveBuildResult, { acting?: BuildActing } & RemoveBuildBody>({
      query: ({ acting, ...body }) => ({
        url: '/pc-build/lines',
        method: 'DELETE',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LIST,
    }),
  }),
})

export const {
  useGetBuildsQuery,
  useGetBuildTreeQuery,
  useGetBuildProductsQuery,
  useCreateBuildFromCartMutation,
  useConfirmBuildMutation,
  useOrderBuildMutation,
  useWriteOffBuildMutation,
  useChangeBuildDraftQuantityMutation,
  useRemoveBuildLinesMutation,
} = pcBuildApi
